import React from 'react';

export default function IonizadoSection() {
  return (
    <article className="prose max-w-none" id="calcio-ionico">
      <h2>Cálcio iônico (ionizado): o que é, valores de referência e como interpretar</h2>
      <p>
        <strong>Resposta rápida:</strong> o cálcio iônico (ou ionizado) é a fração livre e ativa do cálcio no
        sangue, cerca de metade do total. É medido diretamente por eletrodo íon-seletivo, em geral numa
        gasometria ou num tubo específico, e é pedido quando o cálcio total pode enganar: albumina alterada,
        doença crítica, transfusão maciça, pós-operatório de paratireoide ou distúrbio do pH. A referência
        típica é <strong>1,12 a 1,32 mmol/L (4,5 a 5,3 mg/dL)</strong>, com variação entre laboratórios.
      </p>

      <h3>Para que serve o exame de cálcio iônico?</h3>
      <p>
        Ele mostra quanto cálcio está de fato disponível para os tecidos, o que importa para a contração
        muscular, a condução cardíaca e a coagulação. Por isso é útil em terapia intensiva, no centro cirúrgico,
        em pacientes com doença renal ou hepática, em transfusões maciças (o citrato do sangue estocado liga
        cálcio) e quando o cálcio total e a albumina não contam a mesma história. Diferente do cálcio total, o
        iônico não depende da albumina, mas depende do pH da amostra.
      </p>

      <h3>Qual o valor de referência do cálcio iônico?</h3>
      <div className="table-scroll">
        <table className="ion-table">
          <caption>Valores de referência típicos do cálcio iônico e do cálcio total em adultos</caption>
          <thead>
            <tr><th scope="col">Medida</th><th scope="col">mmol/L</th><th scope="col">mg/dL</th></tr>
          </thead>
          <tbody>
            <tr><th scope="row">Cálcio iônico</th><td>1,12 a 1,32</td><td>4,5 a 5,3</td></tr>
            <tr><th scope="row">Cálcio total</th><td>2,12 a 2,62</td><td>8,5 a 10,5</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        Para converter, multiplique mmol/L por 4,008 para obter mg/dL (ou por 2 para mEq/L). Cada laboratório
        e cada analisador tem a sua faixa: use sempre a referência impressa no laudo.
      </p>

      <h3>O que significa cálcio iônico baixo?</h3>
      <p>
        Cálcio iônico abaixo da referência (hipocalcemia ionizada) pode vir de hipoparatireoidismo (inclusive
        após cirurgia de tireoide ou paratireoide), deficiência de vitamina D, hipomagnesemia, pancreatite
        aguda, sepse e doença crítica, doença renal com retenção de fosfato, transfusão maciça (citrato) e
        alcalose, como na hiperventilação. Os sinais clássicos são formigamento nos lábios e nas extremidades,
        câimbras, tetania, sinais de Chvostek e Trousseau, QT longo no ECG e, nos casos graves, convulsão.
        Sintomas ou valores muito baixos pedem avaliação médica imediata.
      </p>

      <h3>O que significa cálcio iônico alto?</h3>
      <p>
        Cálcio iônico acima da referência (hipercalcemia ionizada) tem como causa mais comum, no
        ambulatório, o hiperparatireoidismo primário; no paciente internado, pesam neoplasias (por
        metástases ósseas ou PTHrP), excesso de vitamina D ou de cálcio, tiazídicos, lítio, doenças
        granulomatosas e imobilização prolongada. Pode causar sede e poliúria, constipação, náuseas, litíase
        renal, dor óssea, cansaço e alteração do humor ou confusão, e encurta o QT no ECG. Valores muito
        elevados ou sintomas importantes são urgência.
      </p>

      <h3>Dá para calcular o cálcio iônico?</h3>
      <p>
        Não com precisão. Existem equações que estimam o iônico a partir do cálcio total, da albumina e do
        pH, mas elas concordam mal com a medição direta, sobretudo em pacientes graves e renais. A calculadora
        desta página estima o <strong>cálcio corrigido pela albumina</strong>, que é outra coisa. Se a decisão
        clínica depende do cálcio iônico, a conduta é medi-lo.
      </p>

      <h3>Como é feita a coleta e preciso de jejum?</h3>
      <p>
        A amostra costuma ser de sangue venoso ou arterial coletado sem exposição ao ar, em seringa ou tubo
        apropriado para eletrólitos (com heparina balanceada ou gel), com garrote o mais breve possível,
        porque garrote e punho fechado prolongados acidificam o sangue local e alteram o resultado. Ela deve
        ser processada logo após a coleta, conforme a orientação do laboratório. O jejum geralmente não é
        exigido, mas confirme com quem vai coletar.
      </p>

      <h3>Cálcio iônico ou cálcio total: qual pedir?</h3>
      <p>
        Em pessoa estável, com albumina normal, o cálcio total costuma bastar. O iônico é preferível quando a
        albumina está muito alterada, em doença crítica ou renal, em transfusão maciça, em distúrbios
        ácido-base e no acompanhamento de hipo ou hipercalcemia que mudou a conduta. Informação educativa:
        a interpretação depende do quadro clínico completo e cabe à equipe de saúde.
      </p>
    </article>
  );
}
